import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Button system — inspired by Doctolib, Alan, Stripe.
 *
 * Variants:
 *   primary   = action principale (1 par écran)
 *   secondary = action neutre dark (Stripe-like)
 *   outline   = action emphase modérée
 *   ghost     = action discrète in-context
 *   link      = action textuelle inline
 *   whatsapp  = bouton WhatsApp officiel
 *   danger    = action destructive
 */

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 font-medium select-none whitespace-nowrap",
    "transition-[transform,box-shadow,background,border,opacity] duration-150 ease-[var(--ease-out)]",
    "focus-visible:outline-3 focus-visible:outline-[var(--sahha-primary-300)] focus-visible:outline-offset-2",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-[var(--sahha-primary-600)] text-white shadow-[var(--sahha-shadow-sm)]",
          "hover:bg-[var(--sahha-primary-700)] hover:shadow-[var(--sahha-shadow-hover)] hover:-translate-y-0.5",
          "active:translate-y-0 active:shadow-[var(--sahha-shadow-sm)]",
        ],
        secondary: [
          "bg-[var(--sahha-text-900)] text-white shadow-[var(--sahha-shadow-sm)]",
          "hover:bg-[var(--sahha-text-800)] hover:-translate-y-0.5",
          "active:translate-y-0",
        ],
        outline: [
          "bg-white text-[var(--sahha-primary-700)] border border-[var(--sahha-border-strong)]",
          "hover:border-[var(--sahha-primary-600)] hover:bg-[var(--sahha-primary-50)]",
        ],
        ghost: [
          "bg-transparent text-[var(--sahha-text-700)]",
          "hover:bg-[var(--sahha-surface-1)] hover:text-[var(--sahha-text-900)]",
        ],
        link: [
          "bg-transparent text-[var(--sahha-primary-700)] underline-offset-4 hover:underline",
          "shadow-none translate-y-0 px-0",
        ],
        whatsapp: [
          "bg-[#25D366] text-white shadow-[var(--sahha-shadow-sm)]",
          "hover:brightness-95 hover:-translate-y-0.5",
        ],
        danger: [
          "bg-[var(--sahha-danger)] text-white shadow-[var(--sahha-shadow-sm)]",
          "hover:opacity-90 hover:-translate-y-0.5",
        ],
      },
      size: {
        sm: "h-9 min-h-9 px-4 text-sm rounded-full",
        md: "h-11 min-h-11 px-5 text-[15px] rounded-full",
        lg: "h-13 min-h-13 px-7 text-base rounded-full font-semibold",
        xl: "h-15 min-h-15 px-9 text-lg rounded-full font-semibold",
        icon: "h-11 w-11 min-h-11 rounded-full",
        "icon-sm": "h-9 w-9 min-h-9 rounded-full",
      },
      block: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      block: false,
    },
  },
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    children?: ReactNode;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
  };

export function Button({
  className,
  variant,
  size,
  block,
  children,
  leftIcon,
  rightIcon,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(buttonVariants({ variant, size, block }), className)}
      {...props}
    >
      {leftIcon ? (
        <span aria-hidden className="inline-flex shrink-0 items-center">
          {leftIcon}
        </span>
      ) : null}
      {children}
      {rightIcon ? (
        <span aria-hidden className="inline-flex shrink-0 items-center">
          {rightIcon}
        </span>
      ) : null}
    </button>
  );
}

export { buttonVariants };
